import React, { useState } from 'react';

const CostEstimator = () => {
  // 默认单价配置
  const defaultPrices = {
    beads: 0.5, // 珠子单价
    wire: 20, // 线材单价
    clasp: 1.5, // 扣子单价
    spring: 0.8, // 弹簧单价
    string: 5 // 绳子单价
  };

  // 状态管理
  const [quantities, setQuantities] = useState({
    beads: 100,
    wire: 1,
    clasp: 1,
    spring: 1,
    string: 1
  });

  const [prices, setPrices] = useState(defaultPrices);
  const [totalCost, setTotalCost] = useState(0);
  const [costRange, setCostRange] = useState({ min: 0, max: 0 });

  // 计算总成本
  const calculateTotal = React.useCallback(() => {
    const total = Object.keys(quantities).reduce((sum, key) => {
      return sum + (quantities[key] * prices[key]);
    }, 0);

    setTotalCost(total);
    setCostRange({
      min: total * 1.05, // 5% 预留
      max: total * 1.1 // 10% 预留
    });
  }, [quantities, prices]);

  // 处理数量变化
  const handleQuantityChange = (e) => {
    const { name, value } = e.target;
    setQuantities(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
    calculateTotal();
  };

  // 处理单价变化
  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPrices(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
    calculateTotal();
  };

  // 重置为默认值
  const resetToDefaults = () => {
    setQuantities({
      beads: 100,
      wire: 1,
      clasp: 1,
      spring: 1,
      string: 1
    });
    setPrices(defaultPrices);
    calculateTotal();
  };

  // 初始化计算
  React.useEffect(() => {
    calculateTotal();
  }, [calculateTotal]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-center mb-8 text-purple-800">
          手链材料成本估算器
        </h1>
        
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border border-purple-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 材料数量输入 */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-purple-700">
                材料数量
              </h2>
              <div className="space-y-4">
                {[
                  { name: 'beads', label: '珠子数量', unit: '颗' },
                  { name: 'wire', label: '线材长度', unit: '米' },
                  { name: 'clasp', label: '扣子数量', unit: '个' },
                  { name: 'spring', label: '弹簧数量', unit: '个' },
                  { name: 'string', label: '绳子长度', unit: '米' }
                ].map((item) => (
                  <div key={item.name}>
                    <label className="block text-gray-700 mb-1">
                      {item.label}
                    </label>
                    <div className="flex">
                      <input
                        type="number"
                        name={item.name}
                        value={quantities[item.name]}
                        onChange={handleQuantityChange}
                        className="flex-grow px-4 py-2 rounded-l-lg border border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        min="0"
                        step="0.1"
                      />
                      <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-r-lg border border-purple-200">
                        {item.unit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 材料单价输入 */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-purple-700">
                材料单价
              </h2>
              <div className="space-y-4">
                {[
                  { name: 'beads', label: '珠子单价', unit: '元/颗' },
                  { name: 'wire', label: '线材单价', unit: '元/米' },
                  { name: 'clasp', label: '扣子单价', unit: '元/个' },
                  { name: 'spring', label: '弹簧单价', unit: '元/个' },
                  { name: 'string', label: '绳子单价', unit: '元/米' }
                ].map((item) => (
                  <div key={item.name}>
                    <label className="block text-gray-700 mb-1">
                      {item.label}
                    </label>
                    <div className="flex">
                      <input
                        type="number"
                        name={item.name}
                        value={prices[item.name]}
                        onChange={handlePriceChange}
                        className="flex-grow px-4 py-2 rounded-l-lg border border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        min="0"
                        step="0.1"
                      />
                      <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-r-lg border border-purple-200">
                        {item.unit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 重置按钮 */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={resetToDefaults}
              className="px-6 py-2 bg-purple-200 text-purple-800 rounded-lg hover:bg-purple-300 transition-colors duration-200 font-medium"
            >
              重置为默认值
            </button>
          </div>
        </div>

        {/* 结果展示 */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-purple-100">
          <h2 className="text-xl font-semibold mb-6 text-purple-700 text-center">
            成本估算结果
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="text-gray-600 mb-2">基础成本</h3>
              <p className="text-3xl font-bold text-purple-800">¥{totalCost.toFixed(2)}</p>
            </div>
            <div className="bg-pink-50 rounded-lg p-4">
              <h3 className="text-gray-600 mb-2">含5%预留</h3>
              <p className="text-3xl font-bold text-pink-800">¥{costRange.min.toFixed(2)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-gray-600 mb-2">总成本范围</h3>
              <p className="text-3xl font-bold text-blue-800">¥{costRange.min.toFixed(2)} - ¥{costRange.max.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostEstimator;